import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
import type {
  CandidateResult,
  RevalidationDiff,
  StorageTransfer,
  StorageTransferDetail,
  StorageTransferDisposition,
  StorageTransferMove,
  StorageTransferReason,
  StorageTransferStatus,
  StorageCondition,
  TransferPreview,
  TransferScanStep,
  TransferScanVerdict,
} from '@/types/storage'

// ─── Request payload shapes (mirror cgt-backend transfer DTOs) ──────────────

export type PreviewTransferPayload = {
  sourceRootId: string
  targetRootId: string
}

export type CreateTransferPayload = PreviewTransferPayload & {
  /** A `code` from the tenant's reason catalog. */
  reasonCode: string
  /** Required when the catalog row is not a system default. */
  reasonNote?: string
  /** Defaults to the catalog row's disposition when omitted. */
  disposition?: StorageTransferDisposition
  maintenanceEventId?: string
  assigneeId?: string
  /**
   * Rows changed on the preview before the draft existed (z8nrz7f2gh), each
   * named by the occupant it carries. Checked by the server exactly as a
   * retarget on a draft is.
   */
  moves?: TransferMoveEdit[]
}

export type TransferMoveEdit = {
  lotId?: string
  productId?: string
  targetNodeRef?: string
  exclude?: boolean
  skipReason?: string
}

export type TransferDestinationsParams = {
  unitId: string
  /** The lot being placed, so only slots at its material's condition come back. */
  lotId?: string
  /** The product being placed, so only slots at its mapped storage condition come back. */
  productId?: string
  q?: string
  take?: number
}

/** Free positions one occupant could be planned into (the Moves step picker). */
export type TransferDestinations = {
  unit: { id: string; code: string; path: string }
  requires: StorageCondition | null
  /** How many matched, before the cap. */
  total: number
  slots: { ref: string; path: string }[]
}

/** The three scans a confirmed move carries, sent again so the server checks them again. */
export type ConfirmTransferMovePayload = {
  sourceCode: string
  itemCode: string
  targetCode: string
  /** Camera or keyboard, per scan, so the trail can tell them apart. */
  sourceMethod?: TransferScanMethod
  itemMethod?: TransferScanMethod
  targetMethod?: TransferScanMethod
}

export type TransferScanMethod = 'SCAN' | 'MANUAL'

export type UpdateTransferPayload = {
  reasonCode?: string
  reasonNote?: string
  disposition?: StorageTransferDisposition
  maintenanceEventId?: string
  assigneeId?: string
}

export type UpdateTransferMovePayload = {
  /** A node id, or a `v:<parentId>:<row>:<col>` declared-cell id. */
  targetNodeRef?: string
  /** Take this occupant out of the plan. Requires `skipReason`. */
  exclude?: boolean
  skipReason?: string
}

export type ListTransfersParams = {
  status?: StorageTransferStatus
  sourceEquipmentId?: string
}

export type CreateTransferReasonPayload = {
  code: string
  label: string
  disposition: StorageTransferDisposition
  order?: number
}

export type UpdateTransferReasonPayload = Partial<CreateTransferReasonPayload>

async function request<T>(path: string, tenantId: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders({
      'Content-Type': 'application/json',
      'X-Tenant-Id': tenantId,
      ...init?.headers,
    }),
  })
  handleUnauthorized(res.status)
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { message?: string | string[] } | null
    const message = Array.isArray(body?.message) ? body.message.join('; ') : body?.message
    throw new Error(message || `Request failed: ${res.status}`)
  }
  return res.json() as Promise<T>
}

function query(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) search.set(key, String(value))
  }
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

const TRANSFERS = '/inventory/storage-transfers'
const REASONS = '/inventory/storage-transfer-reasons'

/**
 * Refuse to build a URL out of a missing id.
 *
 * An empty id interpolates to `…/storage-transfers//ready`, which the API
 * answers with "Cannot POST" — a 404 that says nothing about the real fault.
 * That shipped once: the wizard read the id out of React state in the same
 * tick as the `setState` that would have supplied it, so the transfer was
 * created and then approved with `''`. The callers pass the id per call now;
 * this is what makes the next instance say what happened.
 */
function requireId(kind: 'transfer' | 'move', operation: string, id: string): string {
  const trimmed = id?.trim() ?? ''
  if (!trimmed) {
    throw new Error(`Cannot ${operation} without a ${kind} id`)
  }
  return trimmed
}

export const storageTransfersApi = {
  list: (tenantId: string, params: ListTransfersParams = {}) =>
    request<StorageTransfer[]>(`${TRANSFERS}${query({ ...params })}`, tenantId),

  get: async (tenantId: string, id: string) =>
    request<StorageTransferDetail>(`${TRANSFERS}/${requireId('transfer', 'read a plan', id)}`, tenantId),

  /**
   * Units this one could be transferred into, best fit first.
   *
   * Condition-incompatible units are withheld by the server, not ranked down,
   * and the result says how many — so the UI can explain the absence.
   */
  candidates: async (tenantId: string, sourceRootId: string) =>
    request<CandidateResult>(
      `${TRANSFERS}/candidates${query({ sourceRootId: requireId('transfer', 'list candidates', sourceRootId) })}`,
      tenantId,
    ),

  /** Free, compatible positions in one unit for one occupant (z8nrz7f2gh). */
  destinations: (tenantId: string, params: TransferDestinationsParams) =>
    request<TransferDestinations>(
      `${TRANSFERS}/destinations${query({
        unitId: requireId('transfer', 'list destinations', params.unitId),
        lotId: params.lotId,
        productId: params.productId,
        q: params.q?.trim() || undefined,
        take: params.take !== undefined ? String(params.take) : undefined,
      })}`,
      tenantId,
    ),

  /** Computes the mapping and persists nothing. */
  preview: (tenantId: string, payload: PreviewTransferPayload) =>
    request<TransferPreview>(`${TRANSFERS}/preview`, tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  create: (tenantId: string, payload: CreateTransferPayload) =>
    request<StorageTransferDetail>(TRANSFERS, tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  update: async (tenantId: string, id: string, payload: UpdateTransferPayload) =>
    request<StorageTransfer>(`${TRANSFERS}/${requireId('transfer', 'edit a plan', id)}`, tenantId, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  updateMove: async (tenantId: string, id: string, moveId: string, payload: UpdateTransferMovePayload) =>
    request<StorageTransferMove>(
      `${TRANSFERS}/${requireId('transfer', 'edit a move', id)}/moves/${requireId('move', 'edit a move', moveId)}`,
      tenantId,
      { method: 'PATCH', body: JSON.stringify(payload) },
    ),

  ready: async (tenantId: string, id: string) =>
    request<StorageTransfer>(`${TRANSFERS}/${requireId('transfer', 'ready a plan', id)}/ready`, tenantId, {
      method: 'POST',
    }),

  revalidate: async (tenantId: string, id: string) =>
    request<RevalidationDiff>(
      `${TRANSFERS}/${requireId('transfer', 'revalidate a plan', id)}/revalidate`,
      tenantId,
      { method: 'POST' },
    ),

  planReturn: async (tenantId: string, id: string) =>
    request<StorageTransferDetail>(
      `${TRANSFERS}/${requireId('transfer', 'plan a return', id)}/plan-return`,
      tenantId,
      { method: 'POST' },
    ),

  /**
   * A signed act: `signingToken` is the single-use proof from the shared
   * SignatureDialog that the signer just re-entered their password. The server
   * records the person it proves, never a name sent from here.
   */
  cancel: async (tenantId: string, id: string, reason: string, signingToken: string) =>
    request<StorageTransfer>(`${TRANSFERS}/${requireId('transfer', 'cancel a plan', id)}/cancel`, tenantId, {
      method: 'POST',
      body: JSON.stringify({ reason, signingToken }),
    }),

  // ─── Carrying a plan out (z8nrz7e7ak) ─────────────────────────────────────

  /** Check one scan of a move. Nothing moves; a failed scan is recorded. */
  scanMove: async (
    tenantId: string,
    id: string,
    moveId: string,
    step: TransferScanStep,
    code: string,
    method?: TransferScanMethod,
  ) =>
    request<TransferScanVerdict>(
      `${TRANSFERS}/${requireId('transfer', 'check a scan', id)}/moves/${requireId('move', 'check a scan', moveId)}/scan`,
      tenantId,
      { method: 'POST', body: JSON.stringify({ step, code, method }) },
    ),

  /** Record a move as done and move the item. Returns the plan as it now stands. */
  confirmMove: async (tenantId: string, id: string, moveId: string, payload: ConfirmTransferMovePayload) =>
    request<StorageTransferDetail>(
      `${TRANSFERS}/${requireId('transfer', 'confirm a move', id)}/moves/${requireId('move', 'confirm a move', moveId)}/confirm`,
      tenantId,
      { method: 'POST', body: JSON.stringify(payload) },
    ),

  /** Leave an item behind, with a reason. Returns the plan as it now stands. */
  skipMove: async (tenantId: string, id: string, moveId: string, reason: string) =>
    request<StorageTransferDetail>(
      `${TRANSFERS}/${requireId('transfer', 'skip a move', id)}/moves/${requireId('move', 'skip a move', moveId)}/skip`,
      tenantId,
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),

  listReasons: (tenantId: string) => request<StorageTransferReason[]>(REASONS, tenantId),

  createReason: (tenantId: string, payload: CreateTransferReasonPayload) =>
    request<StorageTransferReason>(REASONS, tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateReason: (tenantId: string, id: string, payload: UpdateTransferReasonPayload) =>
    request<StorageTransferReason>(`${REASONS}/${id}`, tenantId, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  deleteReason: (tenantId: string, id: string) =>
    request<StorageTransferReason>(`${REASONS}/${id}`, tenantId, { method: 'DELETE' }),
}
